import { useState, useEffect } from 'react'
import { apiFetch } from '../lib/api'
import { obligationLabel, obligationExplanation, acceptedSourcesLabel } from '../lib/claims'
import { HelpDot } from './guidance/Guidance'

/* Policy — what this depositary demands, and what it accepts as proof.
 *
 * Replaces the v1 Policy Configuration screen, which described policy v1:
 * evidence requirements, a doc-type staleness map, custom fields and an
 * auto-confirm threshold. `POLICY_V2_CUTOVER` defaults True and no live code
 * reads those structures any more, so four of that screen's five tabs edited
 * a machine that was switched off — saving, returning 200, and changing
 * nothing about what the system demands of any object. Ale, 2026-07-29:
 * "the new claim model is not reflected there so I do not understand what I
 * am looking at."
 *
 * This screen reads the catalogue the engine ACTUALLY runs on
 * (GET /api/policy/obligations) and renders it in the same claims vocabulary
 * the chase and sign-off cards use — same `obligationLabel` /
 * `acceptedSourcesLabel` helpers — so the admin surface and the work surface
 * cannot drift apart.
 *
 * Read-only on purpose. PATCH /api/policy/obligations/{code} exists and is
 * manager-gated, but editing policy is a decision with regulatory weight; a
 * screen that shows the truth is worth shipping before a screen that changes
 * it. Honest omission beats a control whose effect the officer can't predict.
 */

interface Obligation {
  code: string
  family: string
  level: string
  is_mandatory: boolean
  accepted_sources: string[]
  content_fields: unknown[]
  stale_after_months: number | null
  description: string
}

/** The object each obligation attaches to, in the officer's words. */
const LEVEL_LABEL: Record<string, string> = {
  entity: 'every company and asset on the record',
  entity_or_asset: 'every company and asset on the record',
  position: 'every position',
  instrument: 'every instrument',
  trade: 'every movement',
}

/** Family → the question that family answers. */
const FAMILY_LABEL: Record<string, string> = {
  existence: 'Does it exist?',
  balance: 'Is the quantity right?',
  movement: 'Did the movement happen as stated?',
}

/* What must agree, in claim-wide words (CLAIM-LEG ALIGNMENT C3).
 *
 * `content_fields` stopped being the list of things compared: everything
 * the document asserts about the object is checked against the record. The
 * list only names what the document must SPEAK TO. Printing it as "must
 * match: units" therefore understated the bar; the card now says the rule
 * and names the compulsory fields as an addition, not as the limit. */
function mustSpeakTo(fields: unknown[]): string | null {
  const names = fields
    .map((f) =>
      typeof f === 'string'
        ? f
        : f && typeof f === 'object'
          ? String((f as { field?: unknown }).field ?? '')
          : ''
    )
    .filter(Boolean)
    .map((f) => (f === 'tri_quantity' ? 'the amounts' : f.replace(/_/g, ' ')))
  return names.length ? names.join(', ') : null
}

export default function PolicyPage() {
  const [rows, setRows] = useState<Obligation[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    apiFetch<{ obligations: Obligation[] }>('/api/policy/obligations')
      .then((d) => setRows(d.obligations ?? []))
      .catch((e) =>
        setError(e instanceof Error ? e.message : 'could not load the policy catalogue')
      )
  }, [])

  if (error)
    return (
      <main className="detail">
        <div className="dhead">
          <h1>Policy</h1>
        </div>
        <div className="dbody" style={{ padding: 18 }}>
          <div style={{ fontSize: 'var(--text-13)', color: 'var(--gap)' }}>
            The policy catalogue could not be loaded — {error}. Nothing has changed; reload the page
            or try again shortly.
          </div>
        </div>
      </main>
    )

  if (rows === null)
    return (
      <main className="detail">
        <div className="dhead">
          <h1>Policy</h1>
        </div>
        <div className="dbody skeleton-zone" style={{ padding: 18 }}>
          loading the policy catalogue…
        </div>
      </main>
    )

  // Object-first structure (Ale 2026-07-31): sections follow the chain shape —
  // entities, instruments, holdings, movements — with the family question as a
  // per-card chip. Nature/type specifics live in each card's exception rules.
  const LEVELS_ORDER = ['entity', 'entity_or_asset', 'instrument', 'position', 'trade']
  const levelOf = (r: Obligation) => (r.level === 'entity_or_asset' ? 'entity' : r.level)
  const levels = ['entity', 'instrument', 'position', 'trade']
    .filter((l) => rows.some((r) => levelOf(r) === l))
    .concat(
      [...new Set(rows.map(levelOf))].filter(
        (l) =>
          !LEVELS_ORDER.includes(l) && !['entity', 'instrument', 'position', 'trade'].includes(l)
      )
    )
  const LEVEL_TITLE: Record<string, string> = {
    entity: 'Companies & assets',
    instrument: 'Instruments',
    position: 'Positions',
    trade: 'Movements',
  }

  return (
    <main className="detail">
      <div className="dhead">
        <h1>
          Policy <HelpDot k="surface.policy" />
        </h1>
      </div>
      <div
        className="dbody"
        style={{ flexDirection: 'column', gap: 18, padding: 18, overflowY: 'auto' }}
      >
        <div
          style={{
            fontSize: 'var(--text-12)',
            color: 'var(--muted)',
            maxWidth: 760,
            lineHeight: 1.55,
          }}
        >
          What this depositary demands of every object on the record, and what it accepts as proof.
          These are the rules the engine actually runs on: every chase, every sign-off gate and
          every coverage figure comes from this catalogue. <strong>Read-only</strong> — changing
          policy is a decision with regulatory weight and is not made from a screen yet.
        </div>

        {levels.map((lvl) => {
          const group = rows.filter((r) => levelOf(r) === lvl)
          return (
            <section key={lvl} data-testid={`policy-level-${lvl}`}>
              <div style={{ fontSize: 'var(--text-13)', fontWeight: 700, marginBottom: 2 }}>
                {LEVEL_TITLE[lvl] ?? lvl.replace(/_/g, ' ')}
              </div>
              <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)', marginBottom: 10 }}>
                demanded of {LEVEL_LABEL[lvl] ?? lvl} · {group.length} obligation
                {group.length === 1 ? '' : 's'}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {group.map((o) => (
                  <div
                    key={o.code}
                    className="vcard"
                    data-testid="policy-obligation"
                    style={{ padding: 16 }}
                  >
                    <div
                      style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}
                    >
                      <span style={{ fontSize: 'var(--text-13)', fontWeight: 700 }}>
                        {obligationLabel(o.code)}
                      </span>
                      <span className="tag">{o.code}</span>
                      <HelpDot k={`policy.${o.code}`} />
                      <span
                        className={`chip ${o.is_mandatory ? 's-gap' : 's-uncovered'}`}
                        title={
                          o.is_mandatory
                            ? 'The record cannot read as verified while this is open.'
                            : 'Recommended — it does not hold the record back on its own.'
                        }
                      >
                        {o.is_mandatory ? 'mandatory' : 'recommended'}
                      </span>
                      <span
                        style={{
                          fontSize: 'var(--text-12)',
                          color: 'var(--faint)',
                          marginLeft: 'auto',
                        }}
                      >
                        {FAMILY_LABEL[o.family] ?? o.family.replace(/_/g, ' ')}
                      </span>
                    </div>

                    {(o.description || obligationExplanation(o.code)) && (
                      <div
                        style={{
                          fontSize: 'var(--text-12)',
                          color: 'var(--muted)',
                          marginTop: 6,
                          lineHeight: 1.5,
                        }}
                      >
                        {o.description || obligationExplanation(o.code)}
                      </div>
                    )}

                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'max-content 1fr',
                        gap: '5px 14px',
                        marginTop: 10,
                        fontSize: 'var(--text-12)',
                      }}
                    >
                      <span style={{ color: 'var(--faint)' }}>accepted as proof</span>
                      <span>{acceptedSourcesLabel(o.accepted_sources)}</span>

                      <span style={{ color: 'var(--faint)' }}>what must agree</span>
                      <span>
                        everything the document says about the object
                        {mustSpeakTo(o.content_fields)
                          ? ` — and it must state ${mustSpeakTo(o.content_fields)}`
                          : ''}
                      </span>

                      <span style={{ color: 'var(--faint)' }}>expires</span>
                      <span>
                        {o.stale_after_months == null
                          ? 'never — once proved, it stays proved'
                          : `after ${o.stale_after_months} month${o.stale_after_months === 1 ? '' : 's'}`}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )
        })}
      </div>
    </main>
  )
}
